import { useState } from 'react'
import { Button, Popover, Select, Stack } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BILLING_ENTITY_OPTIONS, type BillingEntityValue } from '@/pages/Billing/utils'
import { useOrpc } from '@/hooks/useClient'
import type { StatusPillStyle } from '../utils'

// Copied presentational value (legacy BillingEntityControls pill) — decision: no
// legacy↔v2 component imports across page modules.
const ENTITY_PILL_STYLE: StatusPillStyle = {
  backgroundColor: '#f1f5f9',
  borderColor: '#cbd5e1',
  color: '#475569',
}

type BillingEntityControlProps = {
  encounterId: string
  entity: BillingEntityValue | null
}

/**
 * The Entity pill as a control (port of the legacy BillingEntityControls popover):
 * clicking the pill — or "Set Entity" when the case has none — opens a Select over the
 * three billing entities, confirmed through billingV2UpdateCase (which journals
 * entity_changed and dual-writes the encounter entity tag).
 */
export const BillingEntityControl = ({ encounterId, entity }: BillingEntityControlProps) => {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const [opened, setOpened] = useState(false)
  const [draftEntity, setDraftEntity] = useState<string | null>(entity)
  const updateCase = useMutation(orpc.billingV2UpdateCase.mutationOptions())

  const label = BILLING_ENTITY_OPTIONS.find((option) => option.value === entity)?.label ?? null

  const handleOpenChange = (next: boolean) => {
    if (!next) setDraftEntity(entity)
    setOpened(next)
  }

  const handleConfirm = async () => {
    if (draftEntity !== 'FL' && draftEntity !== 'NJ' && draftEntity !== 'NY') return
    try {
      await updateCase.mutateAsync({ encounterId, billingEntity: draftEntity })
      void queryClient.invalidateQueries({ queryKey: orpc.billingV2ListCases.key() })
      void queryClient.invalidateQueries({ queryKey: orpc.billingV2CaseDetail.key() })
      setOpened(false)
      showNotification({ color: 'green', message: 'Billing entity updated' })
    } catch (error) {
      showNotification({
        color: 'red',
        message: error instanceof Error ? error.message : 'Failed to update billing entity',
      })
    }
  }

  return (
    <Popover
      opened={opened}
      onChange={handleOpenChange}
      position="bottom-end"
      shadow="md"
      width={280}
      withArrow
    >
      <Popover.Target>
        {label ? (
          <button
            type="button"
            onClick={() => handleOpenChange(!opened)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 28,
              paddingInline: 12,
              borderRadius: 8,
              border: `1px solid ${ENTITY_PILL_STYLE.borderColor}`,
              backgroundColor: ENTITY_PILL_STYLE.backgroundColor,
              color: ENTITY_PILL_STYLE.color,
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ) : (
          <Button
            variant="outline"
            onClick={() => handleOpenChange(!opened)}
            styles={{
              root: {
                width: 'fit-content',
                height: 28,
                paddingInline: 12,
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
              },
            }}
          >
            Set Entity
          </Button>
        )}
      </Popover.Target>

      <Popover.Dropdown>
        <Stack gap="sm">
          <Select
            label="Billing entity"
            placeholder="Select entity"
            data={BILLING_ENTITY_OPTIONS}
            value={draftEntity}
            onChange={setDraftEntity}
            comboboxProps={{ withinPortal: false }}
          />
          <Button
            onClick={() => void handleConfirm()}
            loading={updateCase.isPending}
            disabled={!draftEntity}
          >
            Confirm
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}
